/**
 * Перенос личности в браузер кошелька и привязка адреса к аккаунту.
 *
 * Постановка задачи. Кошелёк с телефона подключается во встроенном браузере
 * приложения кошелька — для TRON это единственный путь, для Solana в Phantom
 * фактически тоже. Но встроенный браузер кошелька это отдельный WebView со
 * своим localStorage: JWT приложения там отсутствует и появиться не может.
 * Раньше это никак не учитывалось: страница внутри кошелька честно получала
 * адрес и слала его на /api/wallet/link.php, эндпоинт отвечал 401, ошибка
 * уходила в console.warn — и в приложении кошелёк оставался «не подключён» с
 * пустым балансом. Снаружи это выглядело как «подключил, а толку ноль».
 *
 * Решение. Приложение, где человек авторизован, выпускает одноразовый токен
 * привязки и кладёт его в адрес глубокой ссылки. Страница, открывшаяся внутри
 * кошелька, забирает токен из адресной строки и предъявляет его вместо JWT.
 * Привязка появляется на сервере, а приложение видит её через refreshLinked()
 * — и показывает адрес и баланс, даже когда живого соединения с кошельком в
 * нём нет и быть не может.
 *
 * Почему не sessionStorage, как было раньше. Отметка ставилась в браузере, из
 * которого уходили, а читалась в браузере кошелька — это разные хранилища,
 * и доехать она не могла никогда. Адресная строка — единственный канал,
 * который переживает переход между приложениями.
 */

import axios from 'axios';
import { SupportedNetwork } from '../constants/networks';
import { WalletFamily } from './injectedWallets';

export const LINK_TOKEN_PARAM = 'wt_link';
export const LINK_CHAIN_PARAM = 'wt_chain';
export const LINK_FAMILY_PARAM = 'wt_family';

export interface LinkHandoff {
  token: string;
  chain: SupportedNetwork | null;
  family: WalletFamily;
}

export function familyOfNetwork(net: SupportedNetwork): WalletFamily | null {
  if (net === 'tron') return 'tron';
  if (net === 'solana') return 'solana';
  if (net === 'ton') return null;
  return 'evm';
}

/**
 * Семейство кошельков для окна «открыть в приложении кошелька». В отличие от
 * familyOfNetwork здесь нет null: окно всегда нужно чем-то наполнить, а TON
 * подключается своим TonConnect и до этого окна не доходит.
 */
export function walletFamilyOf(net: SupportedNetwork): WalletFamily {
  return familyOfNetwork(net) ?? 'evm';
}

/** Сеть по умолчанию для семейства — когда в ссылке её не оказалось. */
export function defaultNetworkOfFamily(family: WalletFamily): SupportedNetwork {
  if (family === 'tron') return 'tron';
  if (family === 'solana') return 'solana';
  return 'polygon';
}

/**
 * Выпустить токен привязки. Возвращает null, если приложение не авторизовано
 * или сервер недоступен: тогда ссылка уходит без токена и человек внутри
 * кошелька просто увидит обычную страницу — хуже, чем было, не станет.
 */
export async function requestLinkToken(
  chain: SupportedNetwork | null,
  family: WalletFamily
): Promise<string | null> {
  try {
    const res = await axios.post('/api/wallet/link-session.php', { chain: chain || '', family });
    const token = res.data?.token;
    return typeof token === 'string' && token !== '' ? token : null;
  } catch (e) {
    console.warn('[walletLink] failed to issue link token:', e);
    return null;
  }
}

/**
 * Параметры из хвоста адреса (`#...`) — запасной канал.
 *
 * Глубокие ссылки в кошельки строит не браузер, а посредник: Branch у
 * MetaMask, bkapp.vip у Bitget, link.trustwallet.com у Trust. Каждый из них
 * разбирает адрес по-своему, и часть из них считает строку запроса СВОЕЙ:
 * параметры, добавленные к адресу страницы, до кошелька не доезжают. Снаружи
 * это выглядит как «кошелёк открылся, а внутри ничего не происходит»: страница
 * загрузилась без токена, экрана привязки нет, и человек смотрит на обычный
 * сайт, где он к тому же не авторизован.
 *
 * Поэтому токен едет дважды: в строке запроса и в хвосте. Хвост посредники не
 * трогают — он вообще не уходит на сервер, — а стоит это одной строки.
 */
function paramsFromHash(): URLSearchParams | null {
  const raw = window.location.hash.replace(/^#/, '');
  if (raw === '') return null;
  // Хвост может нести маршрут: `#/loans?wt_link=…`. Берём то, что после «?».
  const query = raw.includes('?') ? raw.slice(raw.indexOf('?') + 1) : raw;
  try {
    return new URLSearchParams(query);
  } catch {
    return null;
  }
}

/** Токен из адресной строки — читается уже внутри браузера кошелька. */
export function readLinkHandoff(): LinkHandoff | null {
  if (typeof window === 'undefined') return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }
  if (!params.get(LINK_TOKEN_PARAM)) {
    const fromHash = paramsFromHash();
    if (fromHash?.get(LINK_TOKEN_PARAM)) {
      params = fromHash;
    }
  }
  const token = params.get(LINK_TOKEN_PARAM);
  if (!token) return null;

  const rawFamily = params.get(LINK_FAMILY_PARAM);
  const family: WalletFamily =
    rawFamily === 'tron' || rawFamily === 'solana' || rawFamily === 'evm' ? rawFamily : 'evm';
  const rawChain = params.get(LINK_CHAIN_PARAM);
  const chain = (rawChain as SupportedNetwork) || null;

  return { token, chain, family };
}

/**
 * Убрать токен из адресной строки, не перезагружая страницу.
 * Токен живёт 15 минут; оставлять его в истории и в referrer незачем.
 */
export function stripLinkHandoffFromUrl(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(LINK_TOKEN_PARAM);
    url.searchParams.delete(LINK_CHAIN_PARAM);
    url.searchParams.delete(LINK_FAMILY_PARAM);
    if (url.hash.includes(LINK_TOKEN_PARAM)) {
      // Хвост несёт запасную копию токена — и только её: маршрутизация у
      // приложения на путях, а не на хвосте (BrowserRouter).
      url.hash = '';
    }
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* адрес не разобрался — оставляем как есть */
  }
}

export interface SubmitLinkParams {
  chain: SupportedNetwork;
  address: string;
  walletName: string;
  /** Токен привязки. Без него запрос идёт под JWT приложения. */
  token?: string | null;
}

export type LinkErrorCode =
  | 'link_blocked'
  | 'link_unauthorized'
  | 'link_expired'
  | 'link_already_linked'
  | 'link_invalid_address'
  | 'link_failed';

export class WalletLinkError extends Error {
  code: LinkErrorCode;
  /**
   * Код ответа сервера, если он был. Нужен на экране ошибки: «не удалось
   * привязать» с одним и тем же текстом означало и просроченный токен, и
   * закрытую дверь middleware, и обрыв сети — три разные починки, неотличимые
   * снаружи. Один 403 в отчёте владельца сэкономил бы сессию разбирательств.
   */
  status?: number;
  constructor(code: LinkErrorCode, message?: string, status?: number) {
    super(message || code);
    this.code = code;
    this.status = status;
  }
}

function classify(status: number | undefined, serverError: string | undefined): LinkErrorCode {
  if (serverError === 'errAlreadyLinked') return 'link_already_linked';
  if (serverError === 'errLinkTokenExpired') return 'link_expired';
  // 403 отдаёт security_middleware.php, а не сам эндпоинт: запрос без
  // заголовков приложения. Из встроенного браузера кошелька их взять негде,
  // и до правки 09.09 сюда попадала КАЖДАЯ привязка оттуда.
  if (status === 403) return 'link_blocked';
  if (status === 401) return 'link_unauthorized';
  if (status === 409) return 'link_already_linked';
  if (status === 400) return 'link_invalid_address';
  return 'link_failed';
}

/** Привязать адрес к аккаунту. Бросает WalletLinkError с разбираемым кодом. */
export async function submitLinkedAddress({ chain, address, walletName, token }: SubmitLinkParams): Promise<void> {
  try {
    await axios.post(
      '/api/wallet/link.php',
      // linkToken дублируется в теле: часть встроенных браузеров кошельков
      // режет нестандартные заголовки на кросс-доменном запросе, и заголовок
      // до сервера не доезжает, а тело доезжает всегда.
      token ? { chain, address, walletName, linkToken: token } : { chain, address, walletName },
      token ? { headers: { 'X-Wallet-Link-Token': token } } : undefined
    );
  } catch (e: any) {
    const status = e?.response?.status;
    throw new WalletLinkError(
      classify(status, e?.response?.data?.error),
      e?.response?.data?.message || e?.message,
      typeof status === 'number' ? status : undefined
    );
  }
}
